import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import * as uuid from 'uuid';
import { ValidationError } from 'yup';

import {
  GenerateAiImagesReqBody,
  getProductsManagementApiClient,
} from '../../../../common/services/products-management-api.service';
import { Button } from '../../../../common/ui/button';
import { Container } from '../../../../common/ui/container';
import { FormItem } from '../../../../common/ui/formItem';
import { Modal } from '../../../../common/ui/modal';
import { Notification } from '../../../../common/ui/notification';
import { TextField } from '../../../../common/ui/text-field';
import { productAiImagesValidationSchema, validationMessages } from '../../../../common/validationSchemas';
import { AggPageFormContext } from '../../../../context/page-form-context';
import { useActions, useAppSelector } from '../../../../hooks';
import { aiImagesGenerationStatusSlice } from '../../../../store/reducers/pages/ai-images-generation';
import { TPageForm } from '../../page-form/page-form';
import { ProductAiImage } from './components/ai-image-form-item';
import styles from './product-ai-images.module.scss';

const ProductAiImages = ({ isGetAiImagesLoading }: { isGetAiImagesLoading: boolean }) => {
  const dispatch = useDispatch();
  const { products } = useContext(AggPageFormContext);
  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    setError,
    setValue,
    trigger,
    watch,
  } = useFormContext<TPageForm>();
  const {
    field,
    fieldState,
  } = useController({
    control,
    name: 'productAiImages.prompt',
  });
  const { statuses } = useAppSelector(({ aiImagesGenerationStatus }) => aiImagesGenerationStatus);
  const {
    openConfirmModal,
  } = useActions();

  const {
    append,
    fields,
    remove,
  } = useFieldArray({
    control,
    name: 'productAiImages.ids',
  });

  const prompt = watch('productAiImages.prompt');
  const ids = watch('productAiImages.ids');
  const [preview, setPreview] = useState<string>(null);

  const onProceedClick = async () => {
    try {
      clearErrors(['productAiImages.prompt', 'productAiImages.ids']);
      await productAiImagesValidationSchema.validate(getValues('productAiImages'), { abortEarly: false });
      const isValid = await trigger(['productAiImages']);

      if (!isValid) return;

      openConfirmModal({
        description: 'You are going to generate new AI Image for product. Current image'
          + ' will be replaced with new one. Are you sure you want to proceed?'
          + ' Process will begin immediately after confirmation.',
        successAction: generateProductAiImages,
        title: 'AI Image Generation',
      });
    } catch (err) {
      if (err instanceof ValidationError) {
        if (err.errors.includes(validationMessages.required)) {
          setError('productAiImages.prompt', { message: validationMessages.required });
        } else if (err.errors.includes(validationMessages.noSelectedImages)) {
          setError('productAiImages.ids', { message: validationMessages.noSelectedImages });
        } else {
          toast.error(err.message);
        }
      }
    }
  };

  const generateProductAiImages = async () => {
    try {
      const formData = getValues();
      const aiImagesData: GenerateAiImagesReqBody = {
        asins: formData.productAiImages.ids
          .filter((elem) => elem.regenerate)
          .map((elem) => elem.product_id),
        prompt: formData.productAiImages.prompt,
      };

      const { status } = await getProductsManagementApiClient().generateAiImages(aiImagesData);

      if (status === 201) {
        fields.forEach((field, index) => {
          setValue(`productAiImages.ids.${index}.regenerate`, false);
        });
        setValue('productAiImages.prompt', '');
        dispatch(aiImagesGenerationStatusSlice.actions.checkStatus({
          asins: aiImagesData.asins,
          id: uuid.v4(),
          isCompleted: false,
          status: 'in-progress',
        }));
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        toast.error(err.message);
      }
    }
  };

  useEffect(() => {
    const productIds = products.map((elem) => elem.product_id);
    const fieldsIds = fields.map((elem) => elem.product_id);
    const isNewProductExist = productIds.some((elem) => !fieldsIds.includes(elem));
    const isOldFieldExist = fieldsIds.some((elem) => !productIds.includes(elem));

    if (isNewProductExist) {
      const newProducts = products.filter((elem) => !fieldsIds.includes(elem.product_id));
      newProducts.forEach((elem) => {
        const imageUrl = elem?.ai_images?.img_large || elem?.ai_images?.img_medium;
        append({
          ai_image_url: imageUrl ? `${imageUrl}?t=${Date.now()}` : undefined,
          product_id: elem?.product_id,
          regenerate: false,
          title: elem.title,
        });
      });
    } else if (isOldFieldExist) {
      fields.forEach((elem, i) => {
        if (!productIds.includes(elem.product_id)) {
          remove(i);
        }
      });
    }
  }, [products]);

  return <Container className={styles.root} withoutBorder>
    <FormItem message={fieldState?.error?.message}>
      <TextField
        invalid={Boolean(fieldState?.error?.message)}
        rows={2}
        {...field}
        onChange={(e) => field.onChange(e.target.value)}
        placeholder="Prompt..."
      />
    </FormItem>
    <Container className={styles.container} withoutBorder>
      {fields.map((field, index) => <ProductAiImage
        key={field.product_id}
        index={index}
        inProgress={statuses.some((elem) =>
          elem.asins.includes(field.product_id) && elem.status === 'in-progress') || isGetAiImagesLoading}
        onClick={() => setPreview(field.ai_image_url)}
      />)}
      {Boolean(preview) && <Modal onClose={() => setPreview(null)} open>
        <Modal.Body>
          <img alt="preview" src={preview} />
        </Modal.Body>
        <Modal.Actions>
          <Button onClick={() => setPreview(null)} variant="secondary">Close</Button>
        </Modal.Actions>
      </Modal>}
    </Container>
    {(errors?.productAiImages?.ids?.message || errors?.productAiImages?.ids?.message)
      && <Notification
        fullWidth
        message={errors?.productAiImages?.ids?.message || errors?.productAiImages?.ids?.message}
        type="error"
      />}
    <Button
      disabled={!prompt?.length
        && !ids?.some((elem) => elem.regenerate)}
      onClick={onProceedClick}
      variant="secondary"
    >
      Proceed
    </Button>
  </Container>;
};

export { ProductAiImages };
